import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate, useOutletContext } from "react-router-dom"
import { toast } from "react-toastify"
import { errorMsg } from "../../../../Apis/functions"
import { permissionObj } from "../../../../Helpers/permission"
import routes from "../../../../Routes/routes"
import { saveOrder, saveOrderForRequestMeal } from "../../../../reduxToolkit/FoodieOrders/FoodieOrdersApi"
import { createMealModel, getUserType } from "../Model/MealModel"
import { SUB_VIEW_MODEL, TABS_FOODIE_ORDERS } from "../Model/Models"
import { useOrdersContextProvider } from "./OrdersContextProvider"
import SelectionMealsApp from "./SelectionMealsApp"

/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 */


export const BasketResumeApp = () => {
    const { t } = useTranslation()
    const { meal, setMeal } = useOutletContext();
    const { dishes, handleSelection } = SelectionMealsApp()
    const { setParamsContext } = useOrdersContextProvider()
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const location = useLocation();
    let { state } = location; // Get state from location
    let foodieRequestId = state?.foodieRequestId;
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const contractorId = sessionStorage.getItem('contractorId');
    const supplierId = sessionStorage.getItem('providerId');
    const { getUserFullInformation } = useSelector((state) => state.CommonsSlice);
    const { getFirstFoodieSetting: foodieSetting } = useSelector((state) => state.employeeFoodieSlice);
    const [showBanner, setShowBanner] = useState(true);
    const [costTotal, setCostTotal] = useState(0);
    // Get userId from authentication state
    const userId = useSelector((state) => state.authenticatioauthennSlice.getUserByIdAtEntry.userId)
    const isItems = meal.menuMealTimeIds.length;

    /**
     * Filters grouped courses to only include meals whose IDs are present in menuMealTimeIds.
     * @param {Array} responseData - Array of course objects, each containing meals.
     * @param {Array} menuMealTimeIds - Array of meal time IDs to filter.
     * @returns {Array} Filtered list of courses with meals that match the provided IDs.
     */
    function filterGroupedMealsByMenuIds(responseData, menuMealTimeIds) {
        return responseData
            .map(course => {
                const filteredMeals = course.meals.filter(meal =>
                    menuMealTimeIds.includes(meal.id)
                );

                return filteredMeals.length > 0
                    ? { foodieCourseTypeId: course.foodieCourseTypeId, meals: filteredMeals }
                    : null;
            })
            .filter(Boolean);
    }

    // Memoized list of filtered meals to be shown in the basket summary
    const filteredMeals = filterGroupedMealsByMenuIds(dishes, meal.menuMealTimeIds)


    /**
    * Handles saving the order.
    * - Validates meal time selection.
    * - Dispatches the saveOrder action.
    * - Shows a success message, changes tab, and navigates to the main orders page on success.
    * - Shows an error message on failure.
    */
    const handleSaveOrder = async () => {
        if (!meal.isValidMealTimeModal()) return
        let goTabIndex = TABS_FOODIE_ORDERS.MY_ORDERS.INDEX;

        const userTypeId = getUserType({ id: getUserFullInformation?.id || userId, contractorId, supplierId })

        const body = {
            ...meal.toSaveOrder({
                requestedByUserTypeId: userTypeId,
                userId
            })
        };

        let url = contractorId
            ? routes.contractor.foodie
            : supplierId
                ? routes.suppliers.foodie
                : routes.foodieOrders.main

        try {
            if (foodieRequestId) {
                const aux = meal.toSaveExtraOrder(foodieRequestId);
                goTabIndex = TABS_FOODIE_ORDERS.REQUEST_MEALS.INDEX;
                await dispatch(saveOrderForRequestMeal({ ...aux })).unwrap();
            }
            else
                await dispatch(saveOrder({ ...body })).unwrap();
            toast.success(t('has_been_created_successfully'));
            setMeal(createMealModel());
            if (setParamsContext) {
                setParamsContext(prev => ({
                    ...prev,
                    tabIndex: goTabIndex
                }));
            }
            navigate(url, {
                state: {
                    tabIndex: goTabIndex,
                    subView: SUB_VIEW_MODEL.REQUEST_MEALS.HISTORY,
                },
            });
        } catch (error) {
            const errorData = error?.response?.data || error;
            errorMsg(errorData?.code, { menuName: errorData?.data }, { bold: <strong /> });
        }
    }

    useEffect(() => {
        if (!DEV && !supplierId && !contractorId && !foodieRequestId && !permission?.includes(permissionObj?.WEB_FOODIE_MENU_SELECTION_CREATE)
        ) navigate(routes.forbidden);
    }, [DEV, permission, navigate, contractorId, supplierId])

    useEffect(() => {
        if (filteredMeals?.length > 0) {
            let costTotal;

            if (foodieSetting?.isMealTimeCost) {
                costTotal = filteredMeals?.[0]?.meals?.[0]?.cost || 0;
            } else {
                costTotal = filteredMeals.reduce((sum, item) => {
                    const subtotal = item?.meals?.reduce((subSum, meal) => subSum + meal.cost, 0) || 0;
                    return sum + subtotal;
                }, 0);
            }
            setCostTotal(costTotal);
        }
    }, [filteredMeals]);

    const handleOnBack = () => {
        let path = '';

        if (contractorId) {
            path = routes.contractor.listMeals;
        } else if (supplierId) {
            path = routes.suppliers.listMeals;
        } else {
            path = routes.foodieOrders.listMeals;
        }

        navigate(path, {
            state: state,
        });
    }

    return {
        t,
        isItems,
        filteredMeals,
        handleSaveOrder,
        handleOnBack,
        handleSelection,
        costTotal,
        foodieSetting,
        showBanner,
        setShowBanner
    }
}
